/**
 * ProfessionsPage
 * Main page for browsing all available professions
 */

import { usePendingAction } from '@/shared/hooks'
import { useHomeTour } from '@shared/hooks/tours'
import { ProfessionCard, ProfessionCardSkeleton } from '@features/professions/components'
import { useProfessions } from '@features/professions/hooks'
import { SearchBar } from '@features/search'
import { Button } from '@shared/components/ui/button'
import type { Profession } from '@shared/types'
import { AlertCircle, Search } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

// ============================================================================
// Component
// ============================================================================

export function ProfessionsPage() {
  const navigate = useNavigate()
  const { data: professions, isLoading, error } = useProfessions()
  const [searchTerm, setSearchTerm] = useState('')

  // Initialize home tour with auto-start for first-time visitors
  useHomeTour(true)

  // Check for pending action
  usePendingAction()

  // Navigate to professionals list filtered by this profession
  const handleProfessionClick = async (profession: Profession) => {
    await navigate(`/professionals/${profession._id}`)
  }

  // ============================================================================
  // Loading State
  // ============================================================================

  if (isLoading) {
    return (

      <div className="container mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Profesiones</h1>
          <p className="text-muted-foreground">
            Explorá las categorías de profesionales disponibles
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar profesión..."
              className="w-full"
              debounceMs={0}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground">'0 profesión disponible'</p>
        </div>

        {/* Loading Grid */}
        <div className="flex flex-col gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <ProfessionCardSkeleton key={index} />
          ))}
        </div>
      </div>
    )
  }

  // ============================================================================
  // Error State
  // ============================================================================

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Error al cargar profesiones</h2>
          <p className="text-muted-foreground mb-6">
            Hubo un problema al obtener la lista de profesiones.
          </p>
          <Button onClick={() => window.location.reload()}>Reintentar</Button>
        </div>
      </div>
    )
  }

  // ============================================================================
  // Empty State
  // ============================================================================

  if (!professions || professions.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <Search className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-semibold mb-2">No hay profesiones disponibles</h2>
          <p className="text-muted-foreground mb-6">
            Actualmente no hay profesiones registradas en el sistema.
          </p>
          <Button onClick={() => void navigate('/')}>Volver al inicio</Button>
        </div>
      </div>
    )
  }

  // Filter professions by search term (for future implementation)
  const filteredProfessions = searchTerm
    ? professions.filter((profession) =>
      profession.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : professions

  // ============================================================================
  // Main Content
  // ============================================================================

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Profesiones</h1>
        <p className="text-muted-foreground">
          Explorá las categorías de profesiones disponibles
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-2 flex flex-col gap-4">

        <div className="w-full" data-tour="search-bar">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar profesión..."
            className="w-full"
            debounceMs={0}
          />
        </div>

        {/* <div className="w-full">
          <CitySelector
            value={selectedCity}
            onChange={setSelectedCity}
            className="w-full"
          />
        </div> */}
      </div>

      {/* Stats */}
      <div className="mb-4">
        <p className="text-sm text-muted-foreground text-right">
          {filteredProfessions.length === 1
            ? '1 profesión disponible'
            : `${filteredProfessions.length} profesiones disponibles`}
        </p>
      </div>

      {/* Professions Grid */}
      <div className="flex flex-col gap-6" data-tour="professions-grid">
        {filteredProfessions.map((profession) => (
          profession && (
            <ProfessionCard
              key={profession._id}
              profession={profession}
              onClick={(p) => void handleProfessionClick(p)}
            />
          )
        ))}
      </div>

      {/* No Results */}
      {filteredProfessions.length === 0 && searchTerm && (
        <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
          <Search className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">
            No se encontraron resultados para "{searchTerm}"
          </h3>
          <p className="text-muted-foreground">
            Intentá con otro término de búsqueda
          </p>
        </div>
      )}
    </div>
  )
}
